/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { createContext, useMemo, type ReactNode, use } from 'react';
import { useFeatureFlag } from '@/features/globalConfig';
import { parseRetryConfigString, type ParsedRetryConfig } from '@/lib/retry/parseRetryConfig';
import { type ApiCategory } from '@/config/retryCategories';
import environmentConfig from '@/config/environment';

type RetryConfigContextValue = Record<ApiCategory, ParsedRetryConfig>;

const RetryConfigContext = createContext<RetryConfigContextValue | undefined>(undefined);

const useRetryConfig = (): RetryConfigContextValue => {
  const context = use(RetryConfigContext);
  if (context === undefined) {
    throw new Error('useRetryConfig must be used within a RetryConfigProvider');
  }
  return context;
};

export const useRetryConfigForCategory = (category: ApiCategory): ParsedRetryConfig => {
  const configs = useRetryConfig();
  return configs[category];
};

type RetryConfigProviderProps = {
  children: ReactNode;
};

const getRetryConfigValue = (categoryConfig: string | undefined): ParsedRetryConfig => {
  if (categoryConfig) {
    return parseRetryConfigString(categoryConfig);
  }

  const envConfig = environmentConfig.RETRY_DEFAULT;
  if (envConfig) {
    return parseRetryConfigString(envConfig);
  }

  return { retry: false, retryDelay: undefined };
};

export const RetryConfigProvider = ({ children }: RetryConfigProviderProps) => {
  const retryConfigContent = useFeatureFlag('RETRY_CONFIG_CONTENT') ?? '0:0:false';
  const retryConfigSearch = useFeatureFlag('RETRY_CONFIG_SEARCH') ?? '2:2000:false';

  const value = useMemo<RetryConfigContextValue>(
    () => ({
      CONTENT: getRetryConfigValue(retryConfigContent),
      SEARCH: getRetryConfigValue(retryConfigSearch),
    }),
    [retryConfigContent, retryConfigSearch],
  );

  return <RetryConfigContext value={value}>{children}</RetryConfigContext>;
};
